import { Button, Col, Drawer, Flex, Form, Input, Row, Typography } from 'antd'
import { Check, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { isOwnerRole } from './constants'
import type { EditableWorkspaceRole, RoleFormValues } from './types'

const { Text } = Typography

type DrawerMode = 'create' | 'edit'

type Props = {
  open: boolean
  mode: DrawerMode
  role?: EditableWorkspaceRole
  saving?: boolean
  onClose: () => void
  onSubmit: (values: RoleFormValues) => void
  onDelete?: (role: EditableWorkspaceRole) => void
}

type FormValues = {
  name: string
  codename?: string
  description?: string
}

const ROLE_FORM_ID = 'workspace-role-form'

const RoleDrawerBody = ({
  mode,
  role,
  onSubmit
}: Pick<Props, 'mode' | 'role' | 'onSubmit'>) => {
  const { t } = useTranslation()
  const isEdit = mode === 'edit'
  const isOwner = isOwnerRole(role?.codename)

  const initialValues = useMemo<FormValues>(
    () =>
      isEdit && role
        ? {
            name: role.name,
            codename: role.codename ?? undefined,
            description: role.description ?? undefined
          }
        : { name: '', codename: undefined, description: undefined },
    [isEdit, role]
  )

  return (
    <Form<FormValues>
      id={ROLE_FORM_ID}
      layout="vertical"
      requiredMark="optional"
      disabled={isOwner}
      initialValues={initialValues}
      onFinish={(values) =>
        // `permissions_ids` is deliberately absent: the matrix owns the
        // permission set, so a rename here must not touch it.
        onSubmit({
          name: values.name,
          codename: values.codename || undefined,
          description: values.description || undefined
        })
      }
    >
      <Row gutter={14}>
        <Col span={14}>
          <Form.Item
            name="name"
            label={t('workspace.orgRoles.form.name')}
            rules={[{ required: true }]}
          >
            <Input placeholder={t('workspace.orgRoles.form.namePlaceholder')} />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            name="codename"
            label={t('workspace.orgRoles.form.codename')}
            tooltip={t('workspace.orgRoles.form.codenameHelp')}
          >
            <Input placeholder="sales_manager" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="description"
        label={t('workspace.orgRoles.form.description')}
      >
        <Input.TextArea
          rows={3}
          placeholder={t('workspace.orgRoles.form.descriptionPlaceholder')}
        />
      </Form.Item>

      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('workspace.orgRoles.matrix.permissionsInMatrixHint')}
      </Text>
    </Form>
  )
}

const DrawerFooter = ({
  isEdit,
  role,
  isOwner,
  saving,
  onCancel,
  onDelete
}: {
  isEdit: boolean
  role?: EditableWorkspaceRole
  isOwner: boolean
  saving?: boolean
  onCancel: () => void
  onDelete?: (role: EditableWorkspaceRole) => void
}) => {
  const { t } = useTranslation()
  const canDelete =
    isEdit && role != null && !isOwner && !role.is_admin && onDelete

  return (
    <Flex justify="space-between" align="center">
      {canDelete && role ? (
        <Button
          danger
          size="small"
          icon={<Trash2 size={14} />}
          onClick={() => onDelete?.(role)}
        >
          {t('workspace.orgRoles.actions.delete')}
        </Button>
      ) : (
        <span />
      )}
      <Flex gap={8}>
        <Button onClick={onCancel}>{t('workspace.orgRoles.cancel')}</Button>
        <Button
          type="primary"
          icon={<Check size={14} />}
          loading={saving}
          disabled={isOwner}
          htmlType="submit"
          form={ROLE_FORM_ID}
        >
          {isEdit
            ? t('workspace.orgRoles.saveChanges')
            : t('workspace.orgRoles.createRole')}
        </Button>
      </Flex>
    </Flex>
  )
}

/**
 * Create/edit drawer for a workspace role's identity — name, codename and
 * description. Permissions are granted in the matrix, mirroring the apps side
 * (`RoleEditorDrawer`), so this form never sends `permissions_ids`.
 *
 * The body is mounted only when `open` is true (and the Drawer uses
 * `destroyOnHidden`), so its `initialValues` always reflect the freshest props.
 */
export const RoleDrawer = ({ open, ...rest }: Props) => {
  const { t } = useTranslation()
  const isEdit = rest.mode === 'edit'
  const isOwner = isOwnerRole(rest.role?.codename)

  const titleText = isEdit
    ? `${t('workspace.orgRoles.editRole')} · ${rest.role?.name ?? ''}`
    : t('workspace.orgRoles.newRole')

  return (
    <Drawer
      open={open}
      onClose={rest.onClose}
      size={560}
      destroyOnHidden
      title={titleText}
      footer={
        <DrawerFooter
          isEdit={isEdit}
          role={rest.role}
          isOwner={isOwner}
          saving={rest.saving}
          onCancel={rest.onClose}
          onDelete={rest.onDelete}
        />
      }
    >
      {open ? (
        <RoleDrawerBody
          mode={rest.mode}
          role={rest.role}
          onSubmit={rest.onSubmit}
        />
      ) : null}
    </Drawer>
  )
}
